import type { AlertColor } from '@mui/material';
import { useSession } from '@side/next-auth/react';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useUserPermissionStates } from '@/common/hooks/useUserPermissionStates';

interface Props {
  children: ReactNode;
}

interface LicensedStateContextInterface {
  alertType: AlertColor;
  stateError: string;
  addStateError: (arg: string) => void;
  clearErrors: () => void;
  redirectStateError: string;
  addRedirectStateError: (message: string, type: AlertColor) => void;
  licensedStates: string[];
  statesWithSubmitPermission: (string | undefined)[];
  licensesLoading: boolean;
}

/**
 * This context primarily exists
 */
const LicensedStateContext = createContext<LicensedStateContextInterface>({
  alertType: 'error',
  stateError: '',
  addStateError: (arg: string) => arg,
  clearErrors: () => null,
  redirectStateError: '',
  addRedirectStateError: (_message: string, _type: AlertColor) => null,
  licensedStates: [],
  statesWithSubmitPermission: [],
  licensesLoading: false,
});

export function useLicensedStateContext() {
  return useContext(LicensedStateContext);
}

export const LicensedStateContextProvider = ({ children }: Props) => {
  const [stateError, setStateError] = useState<string>('');
  const [redirectStateError, setRedirectStateError] = useState<string>('');
  const [alertType, setAlertType] = useState<AlertColor>('error');

  const userId = useSession().data?.user?.id;
  const {
    allStates: licensedStates,
    submitAsStates: statesWithSubmitPermission,
    loading: licensesLoading,
  } = useUserPermissionStates({ userId });

  const addStateError = useCallback((newError: string) => {
    setStateError(newError);
  }, []);

  const addRedirectStateError = useCallback(
    (message: string, severity: AlertColor) => {
      setRedirectStateError(message);
      setAlertType(severity);
    },
    [],
  );

  const clearErrors = useCallback(() => {
    setStateError('');
    setRedirectStateError('');
  }, []);

  const contextValue: LicensedStateContextInterface = useMemo(
    () => ({
      stateError,
      addStateError,
      redirectStateError,
      addRedirectStateError,
      clearErrors,
      alertType,
      licensedStates,
      statesWithSubmitPermission,
      licensesLoading,
    }),
    [
      addRedirectStateError,
      addStateError,
      redirectStateError,
      stateError,
      clearErrors,
      alertType,
      licensedStates,
      statesWithSubmitPermission,
      licensesLoading,
    ],
  );

  return (
    <LicensedStateContext.Provider value={contextValue}>
      {children}
    </LicensedStateContext.Provider>
  );
};
